/**
 * Customer resolution hook for looking up and creating customers
 * Follows ADR-003: High Cohesion - Single responsibility: customer CRUD
 */

import { useRef, useCallback } from 'react';
import { ParlantClient } from 'parlant-client';
import { log, logWarn } from '../utils/logger';
import { categorizeError, ErrorCategory } from './useErrorCategorization';

/**
 * Options for useCustomerResolution hook
 */
export interface UseCustomerResolutionOptions {
  /** Server URL */
  serverUrl: string;
  /** Authentication token */
  authToken?: string;
}

/**
 * Return value for useCustomerResolution hook
 */
export interface UseCustomerResolutionReturn {
  /** Resolves customer ID, creating if needed */
  resolveCustomerId: (customerId: string, customerName?: string) => Promise<string | undefined>;
}

/** Sentinel customer IDs that mean anonymous session — never call the customers API. */
const ANONYMOUS_CUSTOMER_IDS = new Set(['guest']);

function isAnonymousCustomerId(customerId: string): boolean {
  return ANONYMOUS_CUSTOMER_IDS.has(customerId.trim().toLowerCase());
}

/**
 * Checks if an error is a 404 Not Found error
 */
function isNotFoundError(error: unknown, statusCode?: number): boolean {
  if (statusCode === 404) {
    return true;
  }
  
  if (error && typeof error === 'object') {
    if ('status' in error && (error as { status?: number }).status === 404) {
      return true;
    }
    if ('statusCode' in error && (error as { statusCode?: number }).statusCode === 404) {
      return true;
    }
    if ('message' in error) {
      const message = String((error as { message?: unknown }).message || '').toLowerCase();
      if (message.includes('404') || 
          message.includes('not found') || 
          (message.includes('item') && message.includes('not found'))) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Custom hook for resolving customer IDs
 * 
 * @param options - Configuration options
 * @returns Customer resolution functions
 */
export function useCustomerResolution({
  serverUrl,
  authToken
}: UseCustomerResolutionOptions): UseCustomerResolutionReturn {
  // Create ParlantClient instance (memoized)
  const clientRef = useRef<ParlantClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new ParlantClient({ environment: serverUrl });
  }

  /**
   * Looks up an existing customer by ID
   */
  const lookupCustomer = useCallback(async (customerId: string): Promise<string | null> => {
    const requestOptions = authToken 
      ? { headers: { 'Authorization': `Bearer ${authToken}` } }
      : {};
    
    try {
      log('Looking up customer by ID:', customerId);
      const customer = await clientRef.current!.customers.retrieve(customerId, requestOptions);
      log('Found existing customer:', customer.id);
      return customer.id;
    } catch (error) {
      const { category, statusCode } = categorizeError(error);
      
      // Throw for permanent errors (except 404)
      if (category === ErrorCategory.PERMANENT && statusCode !== 404) {
        if (statusCode === 403) {
          throw new Error('Access denied: You do not have permission to access customers.');
        }
        if (statusCode === 401) {
          throw new Error('Authentication failed: Invalid or expired token. Please log in again.');
        }
        throw error;
      }
      
      // Return null for 404 (customer not found)
      if (isNotFoundError(error, statusCode)) {
        return null;
      }
      
      // For other errors, log and return null (will create guest session)
      logWarn('Failed to lookup customer:', error);
      return null;
    }
  }, [authToken]);

  /**
   * Creates a new customer
   */
  const createCustomer = useCallback(async (
    customerId: string, 
    customerName: string
  ): Promise<string | undefined> => {
    const cleanServerUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    try {
      log('Creating new customer:', customerId, 'name:', customerName);
      
      const response = await fetch(`${cleanServerUrl}/customers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: customerId,
          name: customerName,
          tags: []
        })
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied: You do not have permission to create customers.');
        }
        if (response.status === 401) {
          throw new Error('Authentication failed: Invalid or expired token. Please log in again.');
        }
        if (response.status === 409) {
          throw new Error('Customer already exists with this ID.');
        }
        if (response.status >= 500) {
          throw new Error(`Server error while creating customer: ${response.status}`);
        }
        throw new Error(`Failed to create customer: ${response.status}`);
      }
      
      const newCustomer = await response.json();
      log('Created new customer:', newCustomer.id);
      return newCustomer.id;
    } catch (error) {
      logWarn('Failed to create customer, will use guest session:', error);
      return undefined;
    }
  }, [serverUrl, authToken]);

  /**
   * Resolves a customer ID - looks up existing or creates new
   */
  const resolveCustomerId = useCallback(async (
    customerId: string,
    customerName?: string
  ): Promise<string | undefined> => {
    const trimmedId = customerId.trim();
    if (!trimmedId || isAnonymousCustomerId(trimmedId)) {
      log('No customer ID provided, using guest session');
      return undefined;
    }

    // Try to find existing customer
    const existingId = await lookupCustomer(trimmedId);
    if (existingId) {
      return existingId;
    }

    // Create new customer
    const effectiveName = customerName?.trim() || trimmedId;
    return createCustomer(trimmedId, effectiveName);
  }, [lookupCustomer, createCustomer]);

  return {
    resolveCustomerId
  };
}

